import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Mountain, Search, LogOut, User, Bell } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAuth = user && typeof user === "object";
  const [pendingCount, setPendingCount] = React.useState(0);

  React.useEffect(() => {
    if (!isAuth) { setPendingCount(0); return; }
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/notifications/summary");
        if (!cancelled) setPendingCount(data.pending_count || 0);
      } catch { /* ignore */ }
    };
    fetchCount();
    const t = setInterval(fetchCount, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [isAuth]);

  return (
    <nav className="mp-nav" data-testid="main-nav">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <Mountain className="w-6 h-6" style={{ color: "var(--mp-terracotta)" }} />
          <span className="font-serif-display text-2xl tracking-tight">MemoryPeaks</span>
        </Link>
        <div className="flex items-center gap-2 sm:gap-4">
          <Link to="/about" className="btn-ghost hidden sm:inline-flex" data-testid="nav-about-link">About</Link>
          <Link to="/search" className="btn-ghost hidden sm:inline-flex items-center gap-1" data-testid="nav-search-link">
            <Search className="w-4 h-4" /> Search
          </Link>
          {isAuth ? (
            <>
              <Link to="/dashboard" className="btn-ghost" data-testid="nav-dashboard-link">My Memories</Link>
              <Link to="/post" className="btn-ghost hidden sm:inline-flex" data-testid="nav-post-link">Post Photo</Link>
              {user.is_admin && (
                <Link to="/admin" className="btn-ghost text-[#8C3A3A]" data-testid="nav-admin-link">Admin</Link>
              )}
              <Link to="/pending" className="btn-ghost relative inline-flex items-center gap-1" data-testid="nav-pending-link" title="Pending photos">
                <Bell className="w-4 h-4"/>
                {pendingCount > 0 && (
                  <span
                    data-testid="pending-badge"
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] flex items-center justify-center font-semibold text-white"
                    style={{ background: "var(--mp-terracotta)", borderRadius: "9px" }}
                  >
                    {pendingCount > 99 ? "99+" : pendingCount}
                  </span>
                )}
              </Link>
              <Link to={`/u/${user.screen_name}`} className="btn-ghost flex items-center gap-1" data-testid="nav-profile-link">
                <User className="w-4 h-4" /> {user.screen_name}
              </Link>
              <button onClick={async () => { await logout(); navigate("/"); }} className="btn-ghost flex items-center gap-1" data-testid="nav-logout-btn">
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn-ghost" data-testid="nav-login-link">Log in</Link>
              <Link to="/register" className="btn-primary" data-testid="nav-signup-link">Sign up</Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
